import { CUSTOMER_ORDER_ACCEPTANCE_RULES_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useCounterpartiesFilter } from '@src/hooks/filters/useCounterpartyFilter';
import { useExecutionStrategiesFilter } from '@src/hooks/filters/useExecutionStrategiesFilter';
import { useOrdTypeFilter } from '@src/hooks/filters/useOrdTypeFilter';
import { useTimeInForceFilter } from '@src/hooks/filters/useTimeInForceFilter';
import { useCustomers } from '@src/hooks/useCustomer';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import {
  ACTION,
  DEFAULT_BULK_EDIT_MAX_ROWS,
  EntityAdmin,
  FormControlSizes,
  useConstant,
  useSecuritiesContext,
  useSymbolsFilter,
  type ICustomerOrderExecutionRule,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { KillSwitch } from './KillSwitch';
import {
  CUSTOMER_ORDER_ACCEPTANCE_RULES_BULK_EDIT_FIELDS,
  CUSTOMER_ORDER_ACCEPTANCE_RULES_COLUMNS,
  getCustomerOrderAcceptanceRulesDrawerOptions,
} from './utils';

export function CustomerOrderAcceptanceRules() {
  const { isAuthorized } = useRoleAuth();
  const { getConfig } = useOrgConfiguration();

  const customers = useCustomers();
  const { securitiesBySymbol } = useSecuritiesContext();

  const counterpartiesFilter = useCounterpartiesFilter();
  const symbolsFilter = useSymbolsFilter();
  const timeInForceFilter = useTimeInForceFilter();
  const executionStrategiesFilter = useExecutionStrategiesFilter();
  const ordTypeFilter = useOrdTypeFilter();
  const availableFilterableProperties = useMemo(() => {
    return [counterpartiesFilter, symbolsFilter, timeInForceFilter, executionStrategiesFilter, ordTypeFilter];
  }, [counterpartiesFilter, executionStrategiesFilter, ordTypeFilter, symbolsFilter, timeInForceFilter]);
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_ORDER_ACCEPTANCE_RULES_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );

  const drawerOptions = useMemo(() => {
    if (!customers) {
      return undefined;
    }
    return getCustomerOrderAcceptanceRulesDrawerOptions({ customers, securitiesBySymbol });
  }, [customers, securitiesBySymbol]);

  const panelActions = useConstant([{ content: <KillSwitch size={FormControlSizes.Small} /> }]);

  return (
    <EntityAdmin<ICustomerOrderExecutionRule>
      title="Order Acceptance Rules"
      subtitle="These rules determine how client orders will be executed. If post-trade hedging is desired, you can set those rules up here."
      entityName="Order Acceptance Rule"
      path="/customer/order-execution-rules"
      entityIDField="RuleID"
      columns={CUSTOMER_ORDER_ACCEPTANCE_RULES_COLUMNS}
      drawerOptions={drawerOptions}
      filterableProperties={filterableProperties}
      externallyControlledFilter={externallyControlledFilter}
      allowAddEntity={isAuthorized(ACTION.DEALER_TRADING)}
      allowEditEntity={isAuthorized(ACTION.DEALER_TRADING)}
      allowDeleteEntity={isAuthorized(ACTION.DEALER_TRADING)}
      panelActions={panelActions}
      persistKey="dealer/order-acceptance-rules"
      allowBulkEdit={true}
      bulkEditFields={CUSTOMER_ORDER_ACCEPTANCE_RULES_BULK_EDIT_FIELDS}
      maxBulkEditRows={getConfig(OrgConfigurationKey.EntityAdminMaxBulkEditRows, DEFAULT_BULK_EDIT_MAX_ROWS)}
    />
  );
}
